import { useEffect } from 'react';
import Box from '@mui/material/Box';
import { cookieManagerConfig } from './config';

/**
 * The cookie manager appends the TrustArc banner to the container and wires the toggle, so
 * both elements have to be in the DOM before init runs, and init must only run once.
 */
let cookieManagerInitialized = false;

/**
 * The cookie manager loads Segment for us, gated on the consent decision, but sends no page view
 * of its own. We send one only once analytics consent is in place ('Analytics' sits under the
 * marketing category), so an undecided visitor is never tracked.
 */
const trackPageViewOnceConsented = (): void => {
    if (!window.BrewCookieManager.isMarketingAllowed()) {
        return;
    }

    // Segment is only loaded when the consent decision read during init allowed at least one
    // destination. The cookie manager never reloads it, so consent granted during this visit is
    // picked up on the next page load rather than retroactively.
    window.analytics?.page();
};

export const CookieBanner: React.FC = () => {
    useEffect(() => {
        // The cookie manager is loaded from a CDN in index.html, so treat it as best effort:
        // failing to show the banner must not take the realm selector down with it.
        if (!window.BrewCookieManager) {
            console.warn('Cookie manager unavailable, skipping consent banner');
            return;
        }

        const handleConsentChanged = (): void => trackPageViewOnceConsented();

        if (!cookieManagerInitialized) {
            cookieManagerInitialized = true;

            window.BrewCookieManager.init(cookieManagerConfig)
                .then(trackPageViewOnceConsented)
                .catch((e) => console.error('Failed to initialise cookie manager', e));
        }

        document.addEventListener('consentChanged', handleConsentChanged);
        return () => document.removeEventListener('consentChanged', handleConsentChanged);
    }, []);

    return (
        <>
            <Box id="consentManagerContainer" />
            {/* Required by the cookie manager. Hidden, as TrustArc's own banner links to the
                preferences dialog and the realm selector has no settings screen to host it. */}
            <Box component="button" hidden id="cookieToggle" type="button" />
        </>
    );
};
